import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, query } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatAmount, formatDate } from "../../lib/format";
import type { Customer, Invoice, ListResponse, Subscription } from "../../lib/types";
import { toMinor } from "./PaymentLinks";
import {
  Button,
  EmptyState,
  ErrorNotice,
  Field,
  Modal,
  PageHeader,
  Pagination,
  SelectField,
  Sheet,
  SheetHeader,
  Skeleton,
  StatusPill,
  Table,
  Td,
  Th,
  Tr,
} from "../../components/ui";

const PAGE_SIZE = 25;

/**
 * Subscriptions bills a customer on a schedule.
 *
 * It does not store a card and charge it silently, because the rails available in this
 * market offer neither card-on-file nor a debit mandate. Each cycle raises an invoice
 * and asks the customer to authorise it — which is said plainly on this page, so a
 * merchant does not promise their customer something the platform cannot do.
 */
export function Subscriptions() {
  const { mode } = useAuth();
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState("");
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);

  const subscriptions = useQuery({
    queryKey: ["subscriptions", mode, offset, status],
    queryFn: () =>
      api.get<ListResponse<Subscription>>(
        `/api/dashboard/subscriptions${query({ mode, limit: PAGE_SIZE, offset, status })}`,
      ),
  });

  const rows = subscriptions.data?.data ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Billing"
        title="Subscriptions"
        description="Bill a customer on a schedule. Each cycle raises an invoice and sends them a link — we never charge a stored card, because the local rails do not offer one."
        actions={<Button variant="primary" onClick={() => setCreating(true)}>New subscription</Button>}
      />

      <Sheet className="settle settle-1 overflow-hidden">
        <SheetHeader
          title="Recurring charges"
          actions={
            <SelectField
              label=""
              aria-label="Filter by status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setOffset(0);
              }}
              options={[
                { value: "", label: "All statuses" },
                { value: "active", label: "Active" },
                { value: "paused", label: "Paused" },
                { value: "past_due", label: "Past due" },
                { value: "cancelled", label: "Cancelled" },
                { value: "completed", label: "Completed" },
              ]}
              className="w-40"
            />
          }
        />

        {subscriptions.isLoading ? (
          <Skeleton rows={5} />
        ) : subscriptions.error ? (
          <ErrorNotice error={subscriptions.error} className="m-5" />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No subscriptions yet"
            description="Set one up for a customer who pays you the same amount every month."
            action={
              <Button variant="primary" onClick={() => setCreating(true)}>
                New subscription
              </Button>
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Subscription</Th>
                  <Th>Customer</Th>
                  <Th align="right">Amount</Th>
                  <Th>Schedule</Th>
                  <Th>Next bill</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((subscription) => (
                  <Tr key={subscription.id} onClick={() => setViewing(subscription.id)}>
                    <Td>
                      <span className="font-medium text-ink-strong">{subscription.name}</span>
                      <span className="mt-0.5 block text-[12px] text-ink-muted">
                        {subscription.cycles_paid} of {subscription.cycles_billed} cycles paid
                      </span>
                    </Td>
                    <Td className="text-ink-muted">{subscription.customer_name || "—"}</Td>
                    <Td align="right" className="tabular">
                      {subscription.currency} {formatAmount(subscription.amount.minor)}
                    </Td>
                    <Td className="text-ink-muted">{subscription.schedule}</Td>
                    <Td className="text-ink-muted">
                      {subscription.next_billing_at ? formatDate(subscription.next_billing_at) : "—"}
                    </Td>
                    <Td>
                      <StatusPill status={subscription.status} />
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              total={subscriptions.data?.total ?? 0}
              limit={PAGE_SIZE}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Sheet>

      <NewSubscription
        open={creating}
        mode={mode}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
        }}
      />

      <SubscriptionDetail
        subscriptionId={viewing}
        onClose={() => setViewing(null)}
        onChanged={() => queryClient.invalidateQueries({ queryKey: ["subscriptions"] })}
      />
    </>
  );
}

function SubscriptionDetail({
  subscriptionId,
  onClose,
  onChanged,
}: {
  subscriptionId: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ["subscription", subscriptionId],
    queryFn: () =>
      api.get<{ subscription: Subscription; invoices: Invoice[] | null }>(
        `/api/dashboard/subscriptions/${subscriptionId}`,
      ),
    enabled: subscriptionId !== null,
  });

  const setStatus = useMutation({
    mutationFn: (next: string) =>
      api.post<Subscription>(`/api/dashboard/subscriptions/${subscriptionId}/status`, {
        status: next,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription", subscriptionId] });
      onChanged();
    },
  });

  const subscription = detail.data?.subscription;
  const invoices = detail.data?.invoices ?? [];

  return (
    <Modal
      open={subscriptionId !== null}
      onClose={onClose}
      title={subscription?.name ?? "Subscription"}
      description={subscription?.customer_name ? `For ${subscription.customer_name}` : undefined}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          {subscription?.status === "active" && (
            <Button loading={setStatus.isPending} onClick={() => setStatus.mutate("paused")}>
              Pause
            </Button>
          )}
          {subscription?.status === "paused" && (
            <Button variant="primary" loading={setStatus.isPending} onClick={() => setStatus.mutate("active")}>
              Resume
            </Button>
          )}
          {subscription && subscription.status !== "cancelled" && subscription.status !== "completed" && (
            <Button
              variant="danger"
              loading={setStatus.isPending}
              onClick={() => setStatus.mutate("cancelled")}
            >
              Cancel
            </Button>
          )}
        </>
      }
    >
      {detail.isLoading ? (
        <Skeleton rows={4} />
      ) : detail.error ? (
        <ErrorNotice error={detail.error} />
      ) : subscription ? (
        <div className="grid gap-4">
          <ErrorNotice error={setStatus.error} />

          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
            <Detail label="Amount">
              {subscription.currency} {formatAmount(subscription.amount.minor)}
            </Detail>
            <Detail label="Schedule">{subscription.schedule}</Detail>
            <Detail label="Started">{formatDate(subscription.start_at)}</Detail>
            <Detail label="Next bill">
              {subscription.next_billing_at ? formatDate(subscription.next_billing_at) : "—"}
            </Detail>
            {subscription.cancel_reason && (
              <Detail label="Cancelled because">{subscription.cancel_reason}</Detail>
            )}
          </dl>

          <div>
            <p className="label mb-2">Cycles</p>
            {invoices.length === 0 ? (
              <p className="text-[13px] text-ink-muted">No cycles have been billed yet.</p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Invoice</Th>
                    <Th>Due</Th>
                    <Th align="right">Total</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <Tr key={invoice.id}>
                      <Td>
                        <span className="font-mono text-[12.5px]">{invoice.number}</span>
                      </Td>
                      <Td className="text-ink-muted">
                        {invoice.due_at ? formatDate(invoice.due_at) : "—"}
                      </Td>
                      <Td align="right" className="tabular">
                        {invoice.currency} {formatAmount(invoice.total.minor)}
                      </Td>
                      <Td>
                        <StatusPill status={invoice.overdue ? "overdue" : invoice.status} />
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="label">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}

function NewSubscription({
  open,
  mode,
  onClose,
  onCreated,
}: {
  open: boolean;
  mode: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    customerId: "",
    name: "",
    currency: "USD",
    amount: "",
    interval: "monthly",
    intervalCount: "1",
    leadDays: "3",
    startAt: "",
  });

  const customers = useQuery({
    queryKey: ["customers", "all"],
    queryFn: () => api.get<ListResponse<Customer>>("/api/dashboard/customers?limit=200"),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<Subscription>("/api/dashboard/subscriptions", {
        mode,
        customer_id: form.customerId,
        name: form.name,
        currency: form.currency,
        amount_minor: toMinor(form.amount) ?? 0,
        interval: form.interval,
        interval_count: Number(form.intervalCount || 1),
        invoice_lead_days: Number(form.leadDays || 0),
        start_at: form.startAt ? `${form.startAt}T09:00:00Z` : null,
      }),
    onSuccess: onCreated,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New subscription"
      description="Each cycle raises an invoice and sends the customer a link to pay it."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
            Create subscription
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <ErrorNotice error={create.error} />

        <SelectField
          label="Customer"
          value={form.customerId}
          onChange={(e) => setForm({ ...form, customerId: e.target.value })}
          options={[
            { value: "", label: "Choose a customer…" },
            ...(customers.data?.data ?? []).map((c) => ({ value: c.id, label: c.display_name })),
          ]}
        />

        <Field
          label="What is this for?"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Gym membership"
          hint="The customer sees this on every invoice."
        />

        <div className="grid gap-4 sm:grid-cols-[7rem_1fr]">
          <SelectField
            label="Currency"
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
            options={[
              { value: "USD", label: "USD" },
              { value: "ZWG", label: "ZWG" },
            ]}
          />
          <Field
            label="Amount per cycle"
            required
            inputMode="decimal"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            placeholder="30.00"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Bill every"
            value={form.interval}
            onChange={(e) => setForm({ ...form, interval: e.target.value })}
            options={[
              { value: "weekly", label: "Week" },
              { value: "monthly", label: "Month" },
              { value: "quarterly", label: "Quarter" },
              { value: "yearly", label: "Year" },
            ]}
          />
          <Field
            label="Number of those"
            inputMode="numeric"
            value={form.intervalCount}
            onChange={(e) => setForm({ ...form, intervalCount: e.target.value })}
            hint="2 with 'month' bills every two months."
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="First bill on"
            type="date"
            value={form.startAt}
            onChange={(e) => setForm({ ...form, startAt: e.target.value })}
            hint="Leave blank to start today."
          />
          <Field
            label="Invoice this many days early"
            inputMode="numeric"
            value={form.leadDays}
            onChange={(e) => setForm({ ...form, leadDays: e.target.value })}
            hint="Gives the customer time to pay before it falls due."
          />
        </div>

        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
